import {
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Image as ImageIcon } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';
import { NavigationPanel } from '@/features/content/NavigationPanel';
import { useNavigationPanelState } from '@/features/content/NavigationPanelState';
import { getQuestionInteractionState } from '@/features/content/hooks/useQuestionInteractionState';
import { QuestionStatsDisplay } from '@/features/content/stats/QuestionStatsDisplay';
import { ContentUnit } from '@/features/content/ContentUnit';
import {
  normalizeComponentResponse,
  type ComponentPageData,
  type NormalizedContentUnit,
  type RawComponent as ContentRawComponent,
} from '@/features/content/useComponent';
import { isKaiCoachComponent } from '@/features/kaiCoach/kaiCoachSection';
import { ExactAnswerQuestion } from '@/features/questions/ExactAnswerQuestion';
import { HintSection } from '@/features/questions/HintSection';
import { MultiPartQuestion } from '@/features/questions/MultiPartQuestion';
import { MultipleChoiceQuestion } from '@/features/questions/MultipleChoiceQuestion';
import { SelectAllQuestion } from '@/features/questions/SelectAllQuestion';
import { NumericalQuestion } from '@/features/questions/NumericalQuestion';
import questionCardStyles from '@/features/questions/QuestionCard.module.css';
import { QuestionContent } from '@/features/questions/QuestionContent';
import questionSectionStyles from '@/features/questions/QuestionSection.module.css';
import { AIUploadInstructorView } from '@/features/questions/aiUpload/AIUploadInstructorView';
import type {
  AIUploadQuestionData,
  ExactAnswerQuestionData,
  MultipleChoiceQuestionData,
  SelectAllQuestionData,
  NormalizedQuestionData,
  NormalizedQuestionSectionData,
  NumericalQuestionData,
} from '@/features/questions/questionNormalizer';
import { SolutionSection } from '@/features/questions/SolutionSection';

import {
  buildAllContentUnitIds,
  buildTitleGroups,
  getCoachContextChapterUids,
  type GenerateCourseComponentSelection,
} from './courseGenerationUtils';
import {
  buildCopyCourseSearch,
  parseCopyCourseParams,
} from './copyCourseParams';
import {
  computeComponentDisplayNumbers,
  formatNumberedTitle,
} from './courseHomeUtils';
import { SectionDivider } from './SectionDivider';
import {
  assignCourseBannerImages,
  getBannerUrlForFilename,
} from './courseBannerImages';
import styles from './CreateCoursePage.module.css';
import { useCreateCourse } from './useCreateCourse';
import { useGenerateCourse } from './useGenerateCourse';
import {
  ALL_SEED_COURSES,
  COURSE_SUBJECT_OPTIONS,
  type SeedCourse,
  useSeedCourses,
} from './useSeedCourses';
import {
  unwrapCourseResponse,
  type RawCourse as SeedCourseResponse,
} from './useCourse';

type CourseType = 'byoc' | 'premium';
type CreateCourseStage = 'details' | 'seedSelection' | 'typeSelection';

interface FieldErrorState {
  courseName?: string;
  courseType?: string;
  general?: string;
  subject?: string;
}

interface RawGetCoursePayload {
  course?: SeedCourseResponse | null;
}

interface RawGetComponentPayload {
  component?: ContentRawComponent | null;
}

interface SeedComponentRow {
  componentId: string;
  errorMessage: string | null;
  isLoading: boolean;
  isSectionDivider: boolean;
  rawComponent: ContentRawComponent | null;
  title: string;
  uid: string;
}

interface ComponentIdentityShape {
  id?: unknown;
  title?: unknown;
  uid?: unknown;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Something went wrong.';
}

// The server answers a chapter id with no chapter stored under it as not
// found: the course lists a chapter that is not there (issue #131).
function isChapterNotStored(error: unknown) {
  return (error as Partial<ApiError> | null)?.status === 404;
}

// What the page says about a copy that failed. A refused copy lists the ticked
// chapters it could not take, by id, and the page has their titles (issue
// #145). Any other failure says what the server said.
function describeCopyFailure(error: unknown, ticked: SeedComponentRow[]) {
  const componentUids = (error as Partial<ApiError> | null)?.details?.component_uids;
  const titles = ticked
    .filter(
      (row) => Array.isArray(componentUids) && componentUids.includes(row.uid),
    )
    .map((row) => row.title);

  return titles.length > 0
    ? `No copy was made. These chapters can't be copied: ${titles.join(', ')}. Reload the page and try again.`
    : getErrorMessage(error);
}

function getComponentIdentifier(component: ComponentIdentityShape, index: number) {
  return (
    normalizeString(component.uid) ??
    normalizeString(component.id) ??
    `seed-component-${index + 1}`
  );
}

function getComponentTitle(component: ComponentIdentityShape, index: number) {
  return normalizeString(component.title) ?? `Untitled chapter ${index + 1}`;
}

function getSeedCourseComponentUids(course: SeedCourseResponse) {
  const orderedUids = course.component_uids ?? course.componentUids ?? [];

  if (Array.isArray(orderedUids)) {
    const normalized = orderedUids
      .map((entry) => normalizeString(entry))
      .filter((entry): entry is string => Boolean(entry));

    if (normalized.length > 0) {
      return normalized;
    }
  }

  const components = Array.isArray(course.components) ? course.components : [];

  return components.map((component, index) => getComponentIdentifier(component, index));
}

function unwrapSeedComponentResponse(
  response:
    | RawGetComponentPayload
    | ContentRawComponent
    | LegacyKuduResponseEnvelope<RawGetComponentPayload | ContentRawComponent>
    | null
    | undefined,
) {
  if (!response || !isRecord(response)) {
    return null;
  }

  if ('Response' in response) {
    return unwrapSeedComponentResponse(
      response.Response as RawGetComponentPayload | ContentRawComponent | null,
    );
  }

  if ('component' in response && isRecord(response.component)) {
    return response.component as ContentRawComponent;
  }

  if ('title' in response || 'id' in response || 'uid' in response) {
    return response as ContentRawComponent;
  }

  return null;
}

function isPreviewSafeImageSrc(src: string) {
  return /^https?:\/\//i.test(src);
}

function sanitizePreviewUnits(units: NormalizedContentUnit[]) {
  return units.filter((unit) => (
    unit.kind !== 'image' || isPreviewSafeImageSrc(unit.src)
  ));
}

function SeedCourseCardImage({
  imageUrl,
  title,
}: {
  imageUrl: string;
  title: string;
}) {
  const [hasImageError, setHasImageError] = useState(false);
  const showImage = Boolean(imageUrl) && !hasImageError;

  return (
    <div aria-hidden="true" className={styles.seedCardMedia}>
      <div className={styles.seedCardImageFallback}>
        <ImageIcon size={28} />
      </div>
      {showImage ? (
        <img
          alt=""
          className={styles.seedCardImage}
          onError={() => setHasImageError(true)}
          src={imageUrl}
          title={title}
        />
      ) : null}
    </div>
  );
}

function getVideoEmbedUrl(videoId: string) {
  return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}`;
}

function formatPointsLabel(points: number) {
  return `${points} point${points === 1 ? '' : 's'}`;
}

function sanitizeQuestionData(question: NormalizedQuestionData): NormalizedQuestionData {
  const promptUnits = sanitizePreviewUnits(question.promptUnits);
  const hint = question.hint
    ? {
        ...question.hint,
        units: sanitizePreviewUnits(question.hint.units),
      }
    : undefined;
  const solution = question.solution
    ? {
        ...question.solution,
        units: sanitizePreviewUnits(question.solution.units),
      }
    : undefined;

  if (question.kind === 'multipleChoice') {
    return {
      ...question,
      hint,
      options: question.options.map((option) => ({
        ...option,
        contentUnits: option.contentUnits
          ? sanitizePreviewUnits(option.contentUnits)
          : undefined,
      })),
      promptUnits,
      solution,
    } satisfies MultipleChoiceQuestionData;
  }

  if (question.kind === 'selectAll') {
    return {
      ...question,
      hint,
      options: question.options.map((option) => ({
        ...option,
        contentUnits: option.contentUnits
          ? sanitizePreviewUnits(option.contentUnits)
          : undefined,
      })),
      promptUnits,
      solution,
    } satisfies SelectAllQuestionData;
  }

  if (question.kind === 'numerical') {
    return {
      ...question,
      hint,
      promptUnits,
      solution,
    } satisfies NumericalQuestionData;
  }

  if (question.kind === 'exactAnswer') {
    return {
      ...question,
      hint,
      promptUnits,
      solution,
    } satisfies ExactAnswerQuestionData;
  }

  if (question.kind === 'aiUpload') {
    return {
      ...question,
      hint,
      promptUnits,
      solution,
    } satisfies AIUploadQuestionData;
  }

  return {
    ...question,
    hint,
    parts: question.parts.map((part) => sanitizeQuestionData(part)),
    promptUnits,
    solution,
  };
}

const noopAnswerHandler = async () => {};

function PreviewQuestionCard({
  question,
}: {
  question: NormalizedQuestionData;
}) {
  const safeQuestion = sanitizeQuestionData(question);
  const interaction = getQuestionInteractionState({
    attemptsAllowed: safeQuestion.maxAttempts,
    attemptsUsed: safeQuestion.attemptsUsed,
    areSolutionsVisible: true,
    hasCheckedAtLeastOnce:
      safeQuestion.attemptsUsed > 0 && safeQuestion.feedback?.status !== 'saved',
    hasSavedAnswer: true,
    hintSetting: safeQuestion.hintSetting,
    isPractice: !safeQuestion.isAssigned,
    isSectionClosed: true,
    isSectionOpen: false,
    role: 'student',
  });
  const showReviewFeedback =
    safeQuestion.isAssigned &&
    interaction.showCorrectIncorrectFeedback &&
    safeQuestion.feedback?.status !== 'saved';
  const showQuestionStats =
    interaction.showEarnedPoints ||
    interaction.showCorrectAnswer ||
    safeQuestion.solutionsAvailableAtUtcMilli !== undefined;
  const hintDisclosure = safeQuestion.hint
    ? {
        ...safeQuestion.hint,
        isVisible: interaction.showHints,
      }
    : undefined;
  const solutionDisclosure = safeQuestion.solution
    ? {
        ...safeQuestion.solution,
        isVisible: interaction.showSolution,
      }
    : undefined;
  const body =
    safeQuestion.kind === 'multipleChoice' ? (
      <MultipleChoiceQuestion
        areSolutionsVisible={true}
        hideStats={false}
        isSectionClosed={true}
        isSectionOpen={false}
        onCheck={noopAnswerHandler}
        onSave={noopAnswerHandler}
        pending={false}
        question={safeQuestion}
        role="student"
        showActions={false}
      />
    ) : safeQuestion.kind === 'numerical' ? (
      <NumericalQuestion
        areSolutionsVisible={true}
        hideStats={false}
        isSectionClosed={true}
        isSectionOpen={false}
        onCheck={noopAnswerHandler}
        onSave={noopAnswerHandler}
        pending={false}
        question={safeQuestion}
        role="student"
        showActions={false}
      />
    ) : safeQuestion.kind === 'exactAnswer' ? (
      <ExactAnswerQuestion
        areSolutionsVisible={true}
        hideStats={false}
        isSectionClosed={true}
        isSectionOpen={false}
        onCheck={noopAnswerHandler}
        onSave={noopAnswerHandler}
        pending={false}
        question={safeQuestion}
        role="student"
        showActions={false}
      />
    ) : safeQuestion.kind === 'aiUpload' ? (
      <AIUploadInstructorView question={safeQuestion} />
    ) : safeQuestion.kind === 'selectAll' ? (
      <SelectAllQuestion
        areSolutionsVisible={true}
        hideStats={false}
        isSectionClosed={true}
        isSectionOpen={false}
        onCheck={noopAnswerHandler}
        onSave={noopAnswerHandler}
        pending={false}
        question={safeQuestion}
        role="student"
        showActions={false}
      />
    ) : (
      <MultiPartQuestion>
        {safeQuestion.parts.map((part) => (
          <PreviewQuestionCard key={part.id} question={part} />
        ))}
      </MultiPartQuestion>
    );

  return (
    <Card
      as="article"
      className={questionCardStyles.card}
      data-assigned={safeQuestion.isAssigned ? 'true' : 'false'}
      data-locked={interaction.isAnswerLocked ? 'true' : 'false'}
      variant="standard"
    >
      <div className={questionCardStyles.header}>
        <h3 className={questionCardStyles.title}>{safeQuestion.label}</h3>
        <div className={questionCardStyles.meta}>
          {!safeQuestion.isAssigned ? (
            <p className={questionCardStyles.practiceTag}>Practice question</p>
          ) : null}
          <p className={questionCardStyles.points}>
            {formatPointsLabel(safeQuestion.pointsPossible)}
          </p>
          {interaction.showEarnedPoints && safeQuestion.scoreEarned !== undefined ? (
            <p className={questionCardStyles.points}>
              Score: {safeQuestion.scoreEarned}/{safeQuestion.pointsPossible}
            </p>
          ) : null}
        </div>
      </div>

      {safeQuestion.promptUnits.length > 0 ? (
        <QuestionContent headingLevel={4} role="student" units={safeQuestion.promptUnits} />
      ) : null}

      {body}

      {showQuestionStats ? (
        <QuestionStatsDisplay
          question={safeQuestion}
          showCorrectAnswer={interaction.showCorrectAnswer}
          showEarnedPoints={interaction.showEarnedPoints}
          showFeedback={showReviewFeedback}
          showStudentAnswer={safeQuestion.isAssigned}
        />
      ) : null}

      <HintSection disclosure={hintDisclosure} headingLevel={4} />
      <SolutionSection disclosure={solutionDisclosure} headingLevel={4} />
    </Card>
  );
}

function relabelPreviewQuestion(
  question: NormalizedQuestionData,
  label: string,
) {
  return {
    ...question,
    label,
  };
}

function PreviewQuestionSection({
  section,
}: {
  section: NormalizedQuestionSectionData;
}) {
  const assignedQuestions = section.questions
    .filter((question) => question.isAssigned)
    .map((question, index) =>
      relabelPreviewQuestion(question, `Question ${index + 1}`),
    );
  const practiceQuestions = section.questions
    .filter((question) => !question.isAssigned)
    .map((question, index) =>
      relabelPreviewQuestion(question, `Practice ${index + 1}`),
    );

  return (
    <section className={questionSectionStyles.section}>
      <div className={questionSectionStyles.header}>
        <div>
          <h2 className={questionSectionStyles.heading}>{section.heading}</h2>
          <p className={questionSectionStyles.summary}>
            {section.questionCount}{' '}
            {section.questionCount === 1 ? 'question' : 'questions'}
          </p>
        </div>
      </div>

      {assignedQuestions.length > 0 ? (
        <ul className={questionSectionStyles.list}>
          {assignedQuestions.map((question) => (
            <li className={questionSectionStyles.listItem} key={question.id}>
              <PreviewQuestionCard question={question} />
            </li>
          ))}
        </ul>
      ) : null}

      {practiceQuestions.length > 0 ? (
        <div className={questionSectionStyles.practiceSection}>
          <h3 className={questionSectionStyles.practiceHeading}>Practice Questions</h3>
          <ul className={questionSectionStyles.list}>
            {practiceQuestions.map((question) => (
              <li className={questionSectionStyles.listItem} key={question.id}>
                <PreviewQuestionCard question={question} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function StageIntro({
  description,
  title,
}: {
  description: string;
  title: string;
}) {
  return (
    <div className={styles.stageIntro}>
      <p className={styles.stageEyebrow}>Create Course</p>
      <h1 className={styles.stageTitle}>{title}</h1>
      <p className={styles.stageDescription}>{description}</p>
    </div>
  );
}

export function CreateCoursePage() {
  const { componentId, seedCourseId } = useParams();
  const { getIdToken, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const createCourse = useCreateCourse();
  const generateCourse = useGenerateCourse();
  const { isNavOpen, toggleNavigation } = useNavigationPanelState();
  const copyMode = useMemo(
    () => parseCopyCourseParams(location.search),
    [location.search],
  );

  const [stage, setStage] = useState<CreateCourseStage>('typeSelection');
  const [courseType, setCourseType] = useState<CourseType | null>(null);
  const [courseName, setCourseName] = useState('');
  const [includeSettings, setIncludeSettings] = useState(false);
  const [subject, setSubject] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [selectedSeedCourse, setSelectedSeedCourse] = useState<SeedCourse | null>(
    null,
  );
  const [fieldErrors, setFieldErrors] = useState<FieldErrorState>({});
  const [selectedComponentMap, setSelectedComponentMap] = useState<
    Record<string, boolean>
  >({});

  const courseNameId = useId();
  const subjectId = useId();
  const subjectFilterId = useId();
  const kuduUserId = getKuduUserId(user) ?? user?.uid ?? undefined;
  const filteredSeedCourses = useSeedCourses(subjectFilter);
  // Banner images for the premium (seed) course cards, using the same
  // subject-based, alternate-within-subject assignment (with default-pool
  // fallback) that real course cards use. The seed data's own `image` field
  // holds legacy relative paths (e.g. "../../images/physics01.jpeg") that don't
  // resolve as frontend-next assets, so premium cards had no banner; this
  // assigns them from the bundled subject header pool instead.
  const seedBannerFilenames = useMemo(
    () =>
      assignCourseBannerImages(
        filteredSeedCourses.map((seedCourse) => ({
          id: seedCourse.id,
          parentSubject: seedCourse.subject,
        })),
      ),
    [filteredSeedCourses],
  );
  const catalogSeedCourse = seedCourseId
    ? (ALL_SEED_COURSES.find((course) => course.id === seedCourseId) ?? null)
    : null;
  const resolvedSeedCourseId =
    seedCourseId ?? selectedSeedCourse?.id ?? copyMode.copyFromCourseId;

  useEffect(() => {
    if (seedCourseId || copyMode.isCopyMode) {
      setCourseType('premium');
    }
  }, [copyMode.isCopyMode, seedCourseId]);

  useEffect(() => {
    if (!copyMode.isCopyMode) {
      return;
    }

    setIncludeSettings(copyMode.includeSettings);

    if (!courseName.trim() && copyMode.courseName) {
      setCourseName(copyMode.courseName);
    }
  }, [
    copyMode.courseName,
    copyMode.includeSettings,
    copyMode.isCopyMode,
    courseName,
  ]);

  useEffect(() => {
    if (
      !copyMode.copyFromCourseId ||
      seedCourseId ||
      componentId ||
      stage !== 'typeSelection'
    ) {
      return;
    }

    navigate(`/app/create/${copyMode.copyFromCourseId}${location.search}`, {
      replace: true,
    });
  }, [
    componentId,
    copyMode.copyFromCourseId,
    location.search,
    navigate,
    seedCourseId,
    stage,
  ]);

  useEffect(() => {
    if (!seedCourseId) {
      return;
    }

    const nextSeedCourse = ALL_SEED_COURSES.find((course) => course.id === seedCourseId);

    if (nextSeedCourse && nextSeedCourse.id !== selectedSeedCourse?.id) {
      setSelectedSeedCourse(nextSeedCourse);
    }
  }, [seedCourseId, selectedSeedCourse?.id]);

  useEffect(() => {
    setSelectedComponentMap({});
  }, [seedCourseId]);

  function createClient() {
    if (!user) {
      throw createDataError('You must be signed in to create a course.');
    }

    const returnTo = `${location.pathname}${location.search}${location.hash}`;

    return createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE_URL || '',
      getIdToken,
      getLegacyToken: async () => createLegacyKuduToken(user),
      onAuthError: () =>
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
          replace: true,
        }),
    });
  }

  const seedCourseQuery = useQuery<SeedCourseResponse | null, ApiError>({
    enabled: Boolean(seedCourseId && user),
    queryFn: async () => {
      if (!seedCourseId) {
        return null;
      }

      const client = createClient();
      const response = await client.invokeKuduMethod<
        RawGetCoursePayload | LegacyKuduResponseEnvelope<RawGetCoursePayload>,
        {
          course_id: string;
          full_response: boolean;
        }
      >('GetCourse', {
        course_id: seedCourseId,
        full_response: true,
      });

      return unwrapCourseResponse(response);
    },
    queryKey: ['create-course-page', 'seed-course', seedCourseId],
    retry: false,
  });

  const seedCourseComponentUids = seedCourseQuery.data
    ? getSeedCourseComponentUids(seedCourseQuery.data)
    : [];
  const resolvedSeedCourseTitle =
    catalogSeedCourse?.name ??
    normalizeString(seedCourseQuery.data?.name) ??
    selectedSeedCourse?.name ??
    'Selected Seed Course';

  useEffect(() => {
    if (!copyMode.isCopyMode || subject.trim()) {
      return;
    }

    const sourceSubject = normalizeString(
      seedCourseQuery.data?.parent_subject ?? seedCourseQuery.data?.parentSubject,
    );

    if (sourceSubject) {
      setSubject(sourceSubject);
      setSubjectFilter(sourceSubject);
    }
  }, [
    copyMode.isCopyMode,
    seedCourseQuery.data?.parentSubject,
    seedCourseQuery.data?.parent_subject,
    subject,
  ]);

  const seedComponentQueries = useQueries({
    queries: seedCourseComponentUids.map((componentUid) => ({
      enabled:
        Boolean(seedCourseId && user) &&
        !seedCourseQuery.isPending &&
        !seedCourseQuery.isError,
      queryFn: async () => {
        if (!seedCourseId) {
          throw createDataError('Choose a seed course before loading chapters.');
        }

        const client = createClient();
        const response = await client.invokeKuduMethod<
          | RawGetComponentPayload
          | LegacyKuduResponseEnvelope<RawGetComponentPayload>,
          {
            component_id: string;
            course_id: string;
          }
        >('GetComponent', {
          component_id: String(componentUid),
          course_id: seedCourseId,
        });

        const rawComponent = unwrapSeedComponentResponse(response);

        if (!rawComponent) {
          throw createDataError(
            'The chapter response did not include component data.',
          );
        }

        return rawComponent;
      },
      queryKey: [
        'create-course-page',
        'seed-component',
        seedCourseId,
        componentUid,
      ],
      retry: false,
    })),
  });

  const componentRows = useMemo<SeedComponentRow[]>(() => {
    return seedCourseComponentUids.map((componentUid, index): SeedComponentRow | null => {
      const query = seedComponentQueries[index];

      if (query?.data) {
        return {
          componentId: componentUid,
          errorMessage: null,
          isLoading: false,
          isSectionDivider:
            normalizeBoolean(
              query.data.is_section_divider ?? query.data.isSectionDivider,
            ) ?? false,
          rawComponent: query.data,
          title: getComponentTitle(query.data, index),
          uid: componentUid,
        };
      }

      if (query?.isError) {
        // Nothing to show or copy. Every row starts checked, so a row for it
        // would hold up Generate Course until it was found and unticked.
        if (isChapterNotStored(query.error)) {
          return null;
        }

        return {
          componentId: componentUid,
          errorMessage: getErrorMessage(query.error),
          isLoading: false,
          isSectionDivider: false,
          rawComponent: null,
          title: `Unavailable chapter ${index + 1}`,
          uid: componentUid,
        };
      }

      return {
        componentId: componentUid,
        errorMessage: null,
        isLoading: true,
        isSectionDivider: false,
        rawComponent: null,
        title: `Loading chapter ${index + 1}`,
        uid: componentUid,
      };
    }).filter((row): row is SeedComponentRow => row !== null);
  }, [seedCourseComponentUids, seedComponentQueries]);

  useEffect(() => {
    if (!seedCourseId || componentRows.length === 0) {
      return;
    }

    setSelectedComponentMap((current) => {
      const allowedKeys = new Set(componentRows.map((row) => row.uid));
      const next: Record<string, boolean> = {};
      let changed = false;

      componentRows.forEach((row) => {
        next[row.uid] = current[row.uid] ?? true;

        if (!(row.uid in current)) {
          changed = true;
        }
      });

      Object.keys(current).forEach((key) => {
        if (!allowedKeys.has(key)) {
          changed = true;
        }
      });

      return changed ? next : current;
    });
  }, [componentRows, seedCourseId]);

  const numberedRows = useMemo(() => {
    return computeComponentDisplayNumbers(
      componentRows.map((row) => ({
        kind: row.isSectionDivider ? 'divider' : 'component',
        row,
      })),
      false,
    );
  }, [componentRows]);

  const previewRow = componentId
    ? componentRows.find((row) => row.componentId === componentId)
    : null;
  const previewComponent = useMemo<ComponentPageData | null>(() => {
    if (!previewRow?.rawComponent || !kuduUserId) {
      return null;
    }

    return normalizeComponentResponse(previewRow.rawComponent, {
      contentId: previewRow.componentId,
      isInstructorRequest: true,
      kuduUserId,
    });
  }, [kuduUserId, previewRow]);

  const isPreviewRoute = Boolean(seedCourseId && componentId);
  const isComponentSelectionRoute = Boolean(seedCourseId && !componentId);

  let announcement = 'Choose how you want to create your course.';

  if (isPreviewRoute) {
    announcement = 'Previewing seed course content.';
  } else if (isComponentSelectionRoute) {
    announcement = 'Select the components to include in your generated course.';
  } else if (copyMode.isCopyMode && stage === 'typeSelection') {
    announcement = 'Preparing the copy flow.';
  } else if (stage === 'details') {
    announcement = 'Enter your course title and subject area.';
  } else if (stage === 'seedSelection') {
    announcement = 'Select a seed course to continue.';
  }

  async function handleCreateByoc() {
    try {
      await createCourse.mutateAsync({
        courseName: courseName.trim(),
        subject: subject.trim(),
      });
    } catch (error) {
      setFieldErrors({
        general: getErrorMessage(error),
      });
    }
  }

  function validateStageTwo() {
    const nextErrors: FieldErrorState = {};

    if (!courseName.trim()) {
      nextErrors.courseName = 'Enter a course name.';
    }

    if (!subject.trim()) {
      nextErrors.subject = 'Choose a subject area.';
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function handleStageOneNext() {
    if (!courseType) {
      setFieldErrors({
        courseType: 'Choose a course type.',
      });
      return;
    }

    setFieldErrors({});
    setStage('details');
  }

  async function handleStageTwoPrimaryAction() {
    if (!validateStageTwo()) {
      return;
    }

    if (courseType === 'byoc') {
      await handleCreateByoc();
      return;
    }

    setFieldErrors({});
    setSubjectFilter(subject.trim());

    if (copyMode.copyFromCourseId) {
      navigate(
        `/app/create/${copyMode.copyFromCourseId}${buildCopyCourseSearch({
          copyFromCourseId: copyMode.copyFromCourseId,
          courseName: courseName.trim(),
          includeSettings,
        })}`,
      );
      return;
    }

    setStage('seedSelection');
  }

  async function handleGenerateCourse() {
    if (!resolvedSeedCourseId) {
      setFieldErrors({
        general: 'Choose a seed course before generating a course.',
      });
      return;
    }

    if (!courseName.trim()) {
      setFieldErrors({
        general: 'Enter course details before generating a course.',
      });
      return;
    }

    const selectedRows = componentRows.filter(
      (row) => selectedComponentMap[row.uid] !== false,
    );

    if (selectedRows.length === 0) {
      setFieldErrors({
        general: 'Select at least one component before generating a course.',
      });
      return;
    }

    const unresolvedRows = selectedRows.filter(
      (row) => !row.isSectionDivider && (!row.rawComponent || row.errorMessage),
    );

    if (unresolvedRows.length > 0) {
      setFieldErrors({
        general: `These chapters are still loading or failed to load: ${unresolvedRows
          .map((row) => row.title)
          .join(', ')}. Untick them or try again.`,
      });
      return;
    }

    const components = selectedRows.map((row) => {
      if (row.isSectionDivider) {
        return {
          isSectionDivider: true,
          selected: true,
          uid: row.uid,
        } satisfies GenerateCourseComponentSelection;
      }

      return {
        allContentUnitIds: buildAllContentUnitIds(row.rawComponent!),
        selected: true,
        uid: row.uid,
      } satisfies GenerateCourseComponentSelection;
    });

    // A new try clears the last answer, so that the next one is announced
    // again even when it is the same refusal.
    setFieldErrors({});
    try {
      await generateCourse.mutateAsync({
        components,
        courseName,
        includeSettings,
        seedCourseId: resolvedSeedCourseId,
      });
    } catch (error) {
      setFieldErrors({
        general: describeCopyFailure(error, selectedRows),
      });
    }
  }

  function handleBack() {
    setFieldErrors({});

    if (isPreviewRoute && seedCourseId) {
      navigate(`/app/create/${seedCourseId}${copyMode.isCopyMode ? location.search : ''}`);
      return;
    }

    if (isComponentSelectionRoute) {
      if (copyMode.isCopyMode) {
        setStage('details');
        navigate(`/app/create${location.search}`);
        return;
      }

      setStage('seedSelection');
      navigate('/app/create');
      return;
    }

    if (stage === 'seedSelection') {
      setStage('details');
      return;
    }

    if (stage === 'details') {
      if (copyMode.isCopyMode) {
        navigate('/app/courses');
        return;
      }

      setStage('typeSelection');
      return;
    }

    navigate('/app/courses');
  }

  function renderTypeSelection() {
    return (
      <Card className={styles.stageSurface}>
        <StageIntro
          description="Choose the course path you want to start with."
          title="Create Course"
        />

        <div className={styles.typeGrid}>
          <button
            className={styles.typeCard}
            data-selected={courseType === 'premium' ? 'true' : 'false'}
            onClick={() => {
              setCourseType('premium');
              setFieldErrors((current) => ({
                ...current,
                courseType: undefined,
                general: undefined,
              }));
            }}
            type="button"
          >
            <h2 className={styles.typeTitle}>Kudu Premium Course</h2>
            <ul className={styles.featureList}>
              <li>Full textbook</li>
              <li>Auto-grading</li>
              <li>Videos</li>
              <li>Low cost $25</li>
            </ul>
          </button>

          <button
            className={styles.typeCard}
            data-selected={courseType === 'byoc' ? 'true' : 'false'}
            onClick={() => {
              setCourseType('byoc');
              setFieldErrors((current) => ({
                ...current,
                courseType: undefined,
                general: undefined,
              }));
            }}
            type="button"
          >
            <h2 className={styles.typeTitle}>Bring Your Own Content</h2>
            <ul className={styles.featureList}>
              <li>Your content</li>
              <li>Always free</li>
            </ul>
          </button>
        </div>

        {fieldErrors.courseType ? (
          <p className={styles.fieldError}>{fieldErrors.courseType}</p>
        ) : null}

        <div className={styles.footer}>
          <Button onClick={() => navigate('/app/courses')} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleStageOneNext}>Next</Button>
        </div>
      </Card>
    );
  }

  function renderDetails() {
    return (
      <Card className={styles.stageSurface}>
        <StageIntro
          description="Name the course and choose the subject area before continuing."
          title="Course Details"
        />

        <div className={styles.detailsRow}>
          <label className={`${styles.field} ${styles.courseNameField}`} htmlFor={courseNameId}>
            <span>Course Name</span>
            <input
              aria-invalid={fieldErrors.courseName ? 'true' : undefined}
              autoComplete="off"
              autoFocus
              className={styles.input}
              id={courseNameId}
              onChange={(event) => {
                setCourseName(event.target.value);
                setFieldErrors((current) => ({
                  ...current,
                  courseName: undefined,
                  general: undefined,
                }));
              }}
              value={courseName}
            />
            {fieldErrors.courseName ? (
              <span className={styles.fieldError}>{fieldErrors.courseName}</span>
            ) : null}
          </label>

          <label className={`${styles.field} ${styles.subjectField}`} htmlFor={subjectId}>
            <span>Subject Area</span>
            <select
              aria-invalid={fieldErrors.subject ? 'true' : undefined}
              className={styles.select}
              id={subjectId}
              onChange={(event) => {
                setSubject(event.target.value);
                setFieldErrors((current) => ({
                  ...current,
                  general: undefined,
                  subject: undefined,
                }));
              }}
              value={subject}
            >
              <option value="">Choose a subject</option>
              {COURSE_SUBJECT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {fieldErrors.subject ? (
              <span className={styles.fieldError}>{fieldErrors.subject}</span>
            ) : null}
          </label>
        </div>

        {fieldErrors.general ? (
          <p className={styles.generalError} role="alert">
            {fieldErrors.general}
          </p>
        ) : null}

        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <Button onClick={() => navigate('/app/courses')} variant="secondary">
              Cancel
            </Button>
            <Button onClick={handleBack} variant="secondary">
              Back
            </Button>
          </div>
          <Button
            onClick={() => {
              void handleStageTwoPrimaryAction();
            }}
          >
            {courseType === 'byoc' ? 'Create Course' : 'Next'}
          </Button>
        </div>
      </Card>
    );
  }

  function renderSeedSelection() {
    return (
      <div className={styles.stageStack}>
        <StageIntro
          description="Select the curated seed course that most closely matches the class you are teaching."
          title={courseName || 'Select Seed Course'}
        />

        <Card className={styles.stageSurface}>
          <label className={styles.field} htmlFor={subjectFilterId}>
            <span>Subject Filter</span>
            <select
              className={styles.select}
              id={subjectFilterId}
              onChange={(event) => setSubjectFilter(event.target.value)}
              value={subjectFilter}
            >
              <option value="">All subjects</option>
              {COURSE_SUBJECT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          {filteredSeedCourses.length === 0 ? (
            <Card className={styles.stateCard}>
              <h2 className={styles.stateTitle}>No seed courses found</h2>
              <p className={styles.stateMessage}>
                Try a different subject filter or go back and change the course details.
              </p>
            </Card>
          ) : (
            <div className={styles.seedGrid}>
              {filteredSeedCourses.map((seedCourse) => (
                <button
                  aria-label={seedCourse.name}
                  className={styles.seedCourseCard}
                  key={seedCourse.id}
                  onClick={() => {
                    setSelectedSeedCourse(seedCourse);
                    setFieldErrors({});
                    navigate(`/app/create/${seedCourse.id}`);
                  }}
                  type="button"
                >
                      <Card className={styles.seedCardSurface} variant="elevated">
                    <SeedCourseCardImage
                      imageUrl={
                        getBannerUrlForFilename(
                          seedBannerFilenames.get(seedCourse.id),
                        ) ?? ''
                      }
                      title={seedCourse.name}
                    />
                    <div className={styles.seedCardContent}>
                        <h2 className={styles.seedCardTitle}>{seedCourse.name}</h2>
                        <p className={styles.seedCardDescription}>
                          {seedCourse.description}
                        </p>
                        <ul className={styles.seedCardFeatures}>
                          {seedCourse.features.map((feature) => (
                            <li key={feature}>{feature}</li>
                          ))}
                        </ul>
                    </div>
                  </Card>
                </button>
              ))}
            </div>
          )}

          <div className={styles.footer}>
            <div className={styles.footerLeft}>
              <Button onClick={() => navigate('/app/courses')} variant="secondary">
                Cancel
              </Button>
              <Button onClick={handleBack} variant="secondary">
                Back
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  function renderComponentSelection() {
    if (!resolvedSeedCourseId) {
      return (
        <Card className={styles.stateCard} role="alert">
          <h2 className={styles.stateTitle}>Seed course not found</h2>
          <p className={styles.stateMessage}>
            The selected seed course could not be found. Go back and choose another course.
          </p>
          <div className={styles.footer}>
            <div className={styles.footerLeft}>
              <Button onClick={() => navigate('/app/courses')} variant="secondary">
                Cancel
              </Button>
              <Button onClick={handleBack} variant="secondary">
                Back
              </Button>
            </div>
          </div>
        </Card>
      );
    }

    if (seedCourseQuery.isPending && componentRows.length === 0) {
      return (
        <Card className={styles.stateCard} role="status">
          <h2 className={styles.stateTitle}>Loading chapters</h2>
          <p className={styles.stateMessage}>
            Fetching the seed course structure and preparing the chapter list.
          </p>
        </Card>
      );
    }

    if (seedCourseQuery.isError) {
      return (
        <Card className={styles.stateCard} role="alert">
          <h2 className={styles.stateTitle}>Unable to load chapters</h2>
          <p className={styles.stateMessage}>
            {getErrorMessage(seedCourseQuery.error)}
          </p>
          <div className={styles.footer}>
            <div className={styles.footerLeft}>
              <Button onClick={() => navigate('/app/courses')} variant="secondary">
                Cancel
              </Button>
              <Button onClick={handleBack} variant="secondary">
                Back
              </Button>
            </div>
          </div>
        </Card>
      );
    }

    return (
      <div className={styles.stageStack}>
        <StageIntro
          description="Choose the chapters you want to include in the generated course."
          title={courseName || 'New Premium Course'}
        />

        <div className={styles.seedCourseMeta}>
          <p className={styles.seedCourseMetaLabel}>Seed Course</p>
          <h2 className={styles.seedCourseMetaTitle}>{resolvedSeedCourseTitle}</h2>
        </div>

        <Card className={styles.stageSurface}>
          <div className={styles.chapterList}>
            {numberedRows.map(({ displayNumber, item }) => {
              const row = item.row;
              const isSelected = selectedComponentMap[row.uid] !== false;
              const sectionCount = row.rawComponent
                ? buildTitleGroups(row.rawComponent).length
                : null;
              const isCoachModule = isKaiCoachComponent(row.rawComponent);
              // The chapters this module's copy will not read, because they
              // are not ticked. Nothing to say when it is not being copied.
              const untickedContextTitles =
                isCoachModule && isSelected && row.rawComponent
                  ? getCoachContextChapterUids(row.rawComponent).flatMap((uid) => {
                      const contextRow = componentRows.find(
                        (candidate) => candidate.uid === uid,
                      );

                      return contextRow && selectedComponentMap[uid] === false
                        ? [contextRow.title]
                        : [];
                    })
                  : [];

              if (row.isSectionDivider) {
                return (
                  <div className={styles.dividerRow} key={row.uid}>
                    <label className={styles.checkboxLabel}>
                      <input
                        aria-label={`Include ${row.title}`}
                        checked={isSelected}
                        className={styles.checkbox}
                        onChange={(event) =>
                          setSelectedComponentMap((current) => ({
                            ...current,
                            [row.uid]: event.target.checked,
                          }))
                        }
                        type="checkbox"
                      />
                      <span className={styles.checkboxText}>Include divider</span>
                    </label>
                    <SectionDivider
                      item={{
                        id: row.uid,
                        kind: 'divider',
                        title: row.title,
                      }}
                    />
                  </div>
                );
              }

              return (
                <div className={styles.chapterRow} key={row.uid}>
                  <label className={styles.chapterCheckboxLabel}>
                    <input
                      aria-label={`Include ${formatNumberedTitle(row.title, displayNumber)}`}
                      checked={isSelected}
                      className={styles.checkbox}
                      onChange={(event) =>
                        setSelectedComponentMap((current) => ({
                          ...current,
                          [row.uid]: event.target.checked,
                        }))
                      }
                      type="checkbox"
                    />
                  </label>

                  <div className={styles.chapterMain}>
                    <div className={styles.chapterText}>
                      <h3 className={styles.chapterTitle}>
                        {formatNumberedTitle(row.title, displayNumber)}
                      </h3>
                      <p className={styles.chapterSubtitle}>
                        {row.isLoading
                          ? 'Loading chapter details...'
                          : sectionCount === null
                            ? 'Chapter details unavailable'
                            : isCoachModule
                              ? 'Kai Coach module'
                              : `${sectionCount} ${sectionCount === 1 ? 'section' : 'sections'} in chapter`}
                      </p>
                      {isCoachModule ? (
                        <p className={styles.chapterSubtitle} role="status">
                          {untickedContextTitles.length > 0
                            ? `Kai won't read these chapters in the copy, because they aren't ticked: ${untickedContextTitles.join(', ')}.`
                            : null}
                        </p>
                      ) : null}
                      {row.errorMessage ? (
                        <p className={styles.componentError}>{row.errorMessage}</p>
                      ) : null}
                    </div>

                    <Button
                      disabled={!row.rawComponent}
                      onClick={() =>
                        navigate(
                          `/app/create/${resolvedSeedCourseId}/${row.componentId}${copyMode.isCopyMode ? location.search : ''}`,
                        )
                      }
                      variant="ghost"
                    >
                      Preview
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {fieldErrors.general ? (
            <p className={styles.generalError} role="alert">
              {fieldErrors.general}
            </p>
          ) : null}

          <div className={styles.footer}>
            <div className={styles.footerLeft}>
              <Button onClick={() => navigate('/app/courses')} variant="secondary">
                Cancel
              </Button>
              <Button onClick={handleBack} variant="secondary">
                Back
              </Button>
            </div>
            <div className={styles.footerRight}>
              {generateCourse.isPending ? (
                <p className={styles.loadingMessage}>Generating course...</p>
              ) : null}
              <Button
                disabled={generateCourse.isPending}
                onClick={() => {
                  void handleGenerateCourse();
                }}
              >
                {generateCourse.isPending ? 'Generating...' : 'Generate Course'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  function renderPreview() {
    if (!seedCourseId) {
      return null;
    }

    // Every chapter has loaded and none is this one: the course does not list
    // it, or it is not stored and the list left it out. Waiting would not end.
    const chaptersLoading =
      seedCourseQuery.isPending ||
      seedComponentQueries.some((query) => query.isPending);

    if (!previewRow && !chaptersLoading) {
      return (
        <Card className={styles.stateCard} role="alert">
          <h2 className={styles.stateTitle}>Unable to preview this chapter</h2>
          <p className={styles.stateMessage}>This chapter isn't available.</p>
          <div className={styles.footer}>
            <Button onClick={handleBack} variant="secondary">
              Back to chapter selection
            </Button>
          </div>
        </Card>
      );
    }

    if (!previewRow || previewRow.isLoading) {
      return (
        <Card className={styles.stateCard} role="status">
          <h2 className={styles.stateTitle}>Loading preview</h2>
          <p className={styles.stateMessage}>
            Fetching seed course content for read-only preview.
          </p>
        </Card>
      );
    }

    if (previewRow.errorMessage || !previewComponent) {
      return (
        <Card className={styles.stateCard} role="alert">
          <h2 className={styles.stateTitle}>Unable to preview this chapter</h2>
          <p className={styles.stateMessage}>
            {previewRow.errorMessage ?? 'The preview data could not be prepared.'}
          </p>
          <div className={styles.footer}>
            <Button onClick={handleBack} variant="secondary">
              Back to chapter selection
            </Button>
          </div>
        </Card>
      );
    }

    return (
      <div className={styles.previewPage}>
        <div className={styles.previewHeader}>
          <Button onClick={handleBack} variant="secondary">
            Back to chapter selection
          </Button>
        </div>

        <Card className={styles.previewBanner} variant="metric">
          <p className={styles.previewBannerEyebrow}>Preview</p>
          <h1 className={styles.previewBannerTitle}>{previewComponent.title}</h1>
          <p className={styles.previewBannerText}>
            You are previewing seed course content. This content is read-only.
          </p>
        </Card>

        <div
          className={styles.previewLayout}
          data-has-nav="true"
          data-nav-open={isNavOpen ? 'true' : 'false'}
        >
          <NavigationPanel
            courseId={seedCourseId}
            courseTitle={previewComponent.title}
            homeHref={`/app/create/${seedCourseId}${copyMode.isCopyMode ? location.search : ''}`}
            homeLabel="Chapter Selection"
            isOpen={isNavOpen}
            onToggle={toggleNavigation}
            tocEntries={previewComponent.tocEntries}
          />

          <div className={styles.previewContentColumn}>
            {previewComponent.videoIds.length > 0 ? (
              <Card className={styles.previewSection}>
                <div className={styles.previewSectionHeader}>
                  <div>
                    <h2 className={styles.previewSectionTitle}>Videos</h2>
                    <p className={styles.previewSectionMeta}>
                      {previewComponent.videoIds.length}{' '}
                      {previewComponent.videoIds.length === 1 ? 'video' : 'videos'}
                    </p>
                  </div>
                </div>

                <div className={styles.videoGrid}>
                  {previewComponent.videoIds.map((videoId) => (
                    <iframe
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className={styles.videoFrame}
                      key={videoId}
                      referrerPolicy="strict-origin-when-cross-origin"
                      src={getVideoEmbedUrl(videoId)}
                      title={`${previewComponent.title} video ${videoId}`}
                    />
                  ))}
                </div>
              </Card>
            ) : null}

            {previewComponent.sections.map((section) => (
              <Card className={styles.previewSection} key={section.id}>
                <div className={styles.previewUnitStack}>
                  {sanitizePreviewUnits(section.units).map((unit) => (
                    <ContentUnit key={unit.id} unit={unit} />
                  ))}
                </div>
              </Card>
            ))}

            {previewComponent.questionSections.map((section) => (
              <PreviewQuestionSection
                key={`${section.kind}-${section.questionSectionId}`}
                section={section}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className={styles.page}>
      <p aria-atomic="true" aria-live="polite" className={styles.liveRegion}>
        {announcement}
      </p>

      {copyMode.isCopyMode && !seedCourseId && !componentId && stage === 'typeSelection' ? (
        <Card className={styles.stateCard} role="status">
          <h2 className={styles.stateTitle}>Preparing copy flow</h2>
          <p className={styles.stateMessage}>
            Loading the selected course into the premium creation flow.
          </p>
        </Card>
      ) : isPreviewRoute
        ? renderPreview()
        : isComponentSelectionRoute
          ? renderComponentSelection()
          : stage === 'typeSelection'
            ? renderTypeSelection()
            : stage === 'details'
              ? renderDetails()
              : renderSeedSelection()}
    </section>
  );
}
